import React from 'react';
import { format } from 'date-fns';
import {
  Box,
  Grid,
  GridItem,
  Stack,
  Text,
  TextLink,
  Heading,
  DialogHeader,
  ButtonLink,
} from '@ads-core/components';
import * as Dialog from '@radix-ui/react-dialog';
import { bandWidthDateFormatter } from 'src/utils/bandwidth/bandWidthDateFormatter';
import { exactDateFormatter, quarterDateFormatter } from 'src/utils/dateFormatter';
import { isPublished } from 'src/utils/isPublished';

import { IntegrationApiFeaturesCongestionReportsContractsResponsesGetCongestionReportsDetailsResponse as CongestionReportDetails } from '@alliander-fe/api';

import { PostalCodesToggle } from './_PostalCodesToggle';
import { FALLBACK_VALUE } from './constant/constants';
import { CongestionReportHeroProps } from '.';

type DialogContentProps = {
  handleCloseDialog: () => void;
  statusLabel?: string;
  map: React.ReactNode;
  report?: string;
} & CongestionReportDetails &
  Pick<
    CongestionReportHeroProps,
    'detailsModalTitle' | 'detailsModalTextLink' | 'detailsModalButtonLabel'
  >;

export const DialogContent = (props: DialogContentProps) => {
  const {
    affectedPostalCodes,
    announcementDate,
    completionDate,
    congestionType,
    detailsModalButtonLabel,
    detailsModalTextLink,
    detailsModalTitle,
    handleCloseDialog,
    locations,
    resolvedDate,
    resolvedStartDate,
    solution,
    status,
    statusLabel,
    map,
    report,
    congestionManagementPublicationDate,
  } = props;

  const expectedEndDateQuarter = bandWidthDateFormatter({
    bandWidthStartDate: resolvedStartDate,
    bandWidthEndDate: resolvedDate,
    callbackFunction: (date) => quarterDateFormatter(date, FALLBACK_VALUE),
  });

  const expectedEndDateExact = bandWidthDateFormatter({
    bandWidthStartDate: resolvedStartDate,
    bandWidthEndDate: resolvedDate,
    callbackFunction: (date) => exactDateFormatter(date, FALLBACK_VALUE),
  });

  const reportIsPublished = isPublished(congestionManagementPublicationDate);

  return (
    <Box paddingInline={{ initial: 8, md: 20 }} paddingBottom={{ initial: 8, md: 14 }}>
      <DialogHeader sticky>
        <Heading size="h3" as="h2">
          {detailsModalTitle}
        </Heading>
      </DialogHeader>

      <Dialog.Description asChild>
        <Stack gap={8} isFullWidth>
          <Grid rowGap={2} columnGap={{ initial: 2, md: 4 }} columns={2}>
            <GridItem columnStart="1" columnEnd="2">
              <Text size="description" weight="bold">
                Plaats:
              </Text>
            </GridItem>
            <GridItem columnStart="2" columnEnd="-1">
              <Text size="description">{locations}</Text>
            </GridItem>

            <GridItem columnStart="1" columnEnd="2">
              <Text size="description" weight="bold">
                Richting:
              </Text>
            </GridItem>
            <GridItem columnStart="2" columnEnd="-1">
              <Text size="description">{congestionType}</Text>
            </GridItem>

            <GridItem columnStart="1" columnEnd="2">
              <Text size="description" weight="bold">
                Aangekondigd:
              </Text>
            </GridItem>
            <GridItem columnStart="2" columnEnd="-1">
              <Text size="description">
                {announcementDate ? format(announcementDate, 'dd-MM-yyyy') : FALLBACK_VALUE}
              </Text>
            </GridItem>

            <GridItem columnStart="1" columnEnd="2">
              <Text size="description" weight="bold">
                Status onderzoek:
              </Text>
            </GridItem>
            <GridItem columnStart="2" columnEnd="-1">
              <Text size="description">{statusLabel ?? FALLBACK_VALUE}</Text>
            </GridItem>
            {completionDate ? (
              <>
                <GridItem columnStart="1" columnEnd="2">
                  <Text size="description" weight="bold">
                    Onderzoek afgerond:
                  </Text>
                </GridItem>
                <GridItem columnStart="2" columnEnd="-1">
                  <Text size="description">
                    {completionDate ? format(completionDate, 'dd-MM-yyyy') : FALLBACK_VALUE}
                  </Text>
                </GridItem>
              </>
            ) : null}
            {status === 'Lopend' ? (
              <>
                <GridItem columnStart="1" columnEnd="2">
                  <Text size="description" weight="bold">
                    Uitbreiding gereed:
                  </Text>
                </GridItem>
                <GridItem columnStart="2" columnEnd="-1">
                  <Text size="description">{expectedEndDateQuarter}</Text>
                </GridItem>
              </>
            ) : null}

            {status !== 'Lopend' ? (
              <>
                <GridItem columnStart="1" columnEnd="2">
                  <Text size="description" weight="bold">
                    Uitbreiding gereed:
                  </Text>
                </GridItem>
                <GridItem columnStart="2" columnEnd="-1">
                  <Text size="description">{expectedEndDateExact}</Text>
                </GridItem>
              </>
            ) : null}

            <GridItem columnStart="1" columnEnd="2">
              <Text size="description" weight="bold">
                Oplossing:
              </Text>
            </GridItem>
            <GridItem columnStart="2" columnEnd="-1">
              <Text size="description">{solution ? solution : FALLBACK_VALUE}</Text>
            </GridItem>

            {affectedPostalCodes ? (
              <>
                <GridItem columnStart="1" columnEnd="2">
                  <Text size="description" weight="bold">
                    Postcodes in dit gebied:
                  </Text>
                </GridItem>
                <GridItem columnStart="2" columnEnd="-1">
                  <PostalCodesToggle postalCodes={affectedPostalCodes} />
                </GridItem>
              </>
            ) : undefined}
          </Grid>
          {detailsModalTextLink?.href || (detailsModalButtonLabel && report) ? (
            <Stack
              alignY={{ initial: 'start', md: 'center' }}
              alignX="start"
              isFullWidth
              direction={{ initial: 'column', md: 'row' }}
              gap={{ initial: 4, md: 6 }}
            >
              {detailsModalButtonLabel && report && reportIsPublished ? (
                <ButtonLink href={report} target="_blank">
                  {detailsModalButtonLabel}
                </ButtonLink>
              ) : null}
              {detailsModalTextLink?.href ? (
                <TextLink href={detailsModalTextLink?.href} onClick={() => handleCloseDialog()}>
                  {detailsModalTextLink?.text}
                </TextLink>
              ) : null}
            </Stack>
          ) : null}
          {map}
        </Stack>
      </Dialog.Description>
    </Box>
  );
};
